// pages/EmployeeLoanEntry.jsx
import { useEffect, useContext, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import HeaderBar from "../../components/HeaderBar";
import PayRollLayoutWrapper from "../../components/PayRollLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  Banknote,
  ArrowLeft,
  Save,
  RefreshCcw,
  Hash,
  Type,
  Calendar,
  DollarSign,
  User,
} from "lucide-react";

const BASE_URL = "http://nissi-india.com"; // change if needed

const EmployeeLoanEntry = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const [searchParams] = useSearchParams();
  const loanIdParam = searchParams.get("id");
  const editingLoanId = loanIdParam && loanIdParam !== "null" ? parseInt(loanIdParam) : null;

  // ✅ consistent button colors
  const primaryBtnClass = `bg-${baseColor}-${baseShade}`;
  const primaryHoverClass = `hover:bg-${baseColor}-700`;

  const finYearId = localStorage.getItem("finYearId") || "";
  const branchId = localStorage.getItem("branchId") || "";
  const userId = localStorage.getItem("loginUserId") || "";

  // Form fields
  const [empCode, setEmpCode] = useState("");
  const [empName, setEmpName] = useState("");
  const [empId, setEmpId] = useState("");
  const [loanCode, setLoanCode] = useState("");
  const [loanAmount, setLoanAmount] = useState("0");
  const [amountPerMonth, setAmountPerMonth] = useState("0");
  const [totalMonth, setTotalMonth] = useState("");
  const [balanceAmount, setBalanceAmount] = useState("");
  const [loanDate, setLoanDate] = useState("");
  
  // Close loan fields
  const [closeLoan, setCloseLoan] = useState(false);
  const [closedDate, setClosedDate] = useState("");
  const [closedAmount, setClosedAmount] = useState("0");
  const [remarks, setRemarks] = useState("");

  // Dropdown data
  const [employees, setEmployees] = useState([]);

  const [loading, setLoading] = useState(false);
  const [empSelectDisabled, setEmpSelectDisabled] = useState(false);

  const screenBadge = useMemo(() => "INFRA SCREEN : C-21/1", []);

  const inputBase =
    `w-full px-4 py-3 rounded-2xl border outline-none transition ` +
    (isDark
      ? "bg-gray-800/40 border-white/10 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500"
      : "bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500");

  const inputDisabled =
    `w-full px-4 py-3 rounded-2xl border outline-none bg-gray-100 dark:bg-gray-700 cursor-not-allowed ` +
    (isDark ? "border-white/10 text-gray-400" : "border-gray-300 text-gray-500");

  const labelBase = `text-sm font-semibold ${isDark ? "text-gray-200" : "text-gray-700"}`;

  const cardBase =
    `rounded-3xl border shadow-xl overflow-hidden ` +
    (isDark
      ? "border-white/10 bg-gradient-to-br from-white/15 to-white/5 backdrop-blur-md"
      : "border-gray-200 bg-white");

  const primaryBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl text-white font-bold shadow-lg ` +
    `hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${primaryBtnClass} ${primaryHoverClass}`;

  const ghostBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl font-bold border transition-all duration-200 ` +
    (isDark
      ? "border-white/10 text-white hover:bg-white/10"
      : "border-gray-200 text-gray-800 hover:bg-gray-50");

  // Get client IP (simplified)
  const getClientIP = () => {
    return "127.0.0.1";
  };

  // Format date to dd/MM/yyyy
  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // Parse dd/MM/yyyy to ISO
  const parseDate = (ddmmyyyy) => {
    if (!ddmmyyyy) return "";
    const [day, month, year] = ddmmyyyy.split("/");
    return `${year}-${month}-${day}`;
  };

  // Load employees dropdown
  const loadEmployees = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/api/employee-loan-entry/employees`, {
        params: {
          finYearId: Number(finYearId),
          branchId: branchId,
        },
      });

      if (res?.data?.success) {
        setEmployees(res.data.rows || []);
      }
    } catch (err) {
      toast.error("Error loading employees");
    }
  };

  // Load loan code
  const loadLoanCode = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/api/employee-loan-entry/generate-loan-code`, {
        params: { finYearId: Number(finYearId) },
      });

      if (res?.data?.success) {
        setLoanCode(res.data.loanCode);
      }
    } catch (err) {
      console.error("Error loading loan code:", err);
    }
  };

  // Handle employee selection change
  const handleEmployeeChange = (selectedEmpId) => {
    const employee = employees.find(emp => emp.fld_Employeeid.toString() === selectedEmpId);
    
    if (employee) {
      setEmpId(employee.fld_Employeeid);
      setEmpCode(employee.fld_Employeecode);
      setEmpName(employee.fld_Employee);
    } else {
      setEmpId("");
      setEmpCode("");
      setEmpName("");
    }
  };

  // Load loan details for editing
  const loadLoanDetails = async () => {
    if (!editingLoanId) return;

    try {
      setLoading(true);
      const res = await axios.get(`${BASE_URL}/api/employee-loan-entry/${editingLoanId}`, {
        params: { finYearId: Number(finYearId) },
      });

      if (res?.data?.success && res.data.loan) {
        const loan = res.data.loan;
        setEmpId(loan.fld_Employeeid);
        setEmpCode(loan.fld_EmployeeCode);
        setEmpName(loan.fld_EmployeeName);
        setLoanCode(loan.fld_LoanCode);
        setLoanAmount(loan.fld_LoanAmount.toString());
        setAmountPerMonth(loan.fld_AmountPerMonth.toString());
        setTotalMonth(loan.fld_TotalMonth.toString());
        setBalanceAmount(loan.fld_BalanceAmount.toString());
        setLoanDate(parseDate(loan.Loandate));
        
        setEmpSelectDisabled(true);
      } else {
        toast.error("Loan not found");
      }
    } catch (err) {
      toast.error("Error loading loan details");
    } finally {
      setLoading(false);
    }
  };

  // Calculate total months when loan amount or amount per month changes
  const calculateTotalMonths = () => {
    const loan = parseFloat(loanAmount) || 0;
    const perMonth = parseFloat(amountPerMonth) || 0;
    
    if (loan > 0 && perMonth > 0) {
      const months = Math.ceil(loan / perMonth);
      setTotalMonth(months.toString());
    }
  };

  useEffect(() => {
    calculateTotalMonths();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loanAmount, amountPerMonth]);

  // Set balance amount when loan amount changes
  useEffect(() => {
    setBalanceAmount(loanAmount);
  }, [loanAmount]);

  // Load last payroll date when closing loan
  const loadLastPayrollDate = async () => {
    if (!empId) return;

    try {
      const res = await axios.get(`${BASE_URL}/api/employee-loan-entry/last-payroll-date/${empId}`, {
        params: { finYearId: Number(finYearId) },
      });

      if (res?.data?.success && res.data.lastPayrollDate) {
        setClosedDate(parseDate(res.data.lastPayrollDate));
      } else {
        setClosedDate(parseDate(formatDate(new Date())));
      }
    } catch (err) {
      setClosedDate(parseDate(formatDate(new Date())));
    }
  };

  // Handle close loan checkbox
  const handleCloseLoanChange = (checked) => {
    setCloseLoan(checked);
    
    if (checked) {
      setClosedAmount(balanceAmount);
      loadLastPayrollDate();
    } else {
      setClosedAmount("0");
      setClosedDate("");
      setRemarks("");
    }
  };

  useEffect(() => {
    loadEmployees();
    if (!editingLoanId) {
      loadLoanCode();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (editingLoanId) {
      loadLoanDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingLoanId]);

  const handleBack = () => {
    window.location.href = "/PayRoll/EmployeeLoanListing";
  };

  const handleReset = () => {
    if (!editingLoanId) {
      setEmpId("");
      setEmpCode("");
      setEmpName("");
      setLoanAmount("0");
      setAmountPerMonth("0");
      setTotalMonth("");
      setBalanceAmount("");
      setLoanDate("");
      setCloseLoan(false);
      setClosedDate("");
      setClosedAmount("0");
      setRemarks("");
      setEmpSelectDisabled(false);
      loadLoanCode();
    }
  };

  const validate = () => {
    if (!empCode) {
      toast.warn("Please Select Employee");
      return false;
    }
    if (!empName) {
      toast.warn("Please Select Employee");
      return false;
    }
    if (!loanAmount || parseFloat(loanAmount) === 0) {
      toast.warn("Please Enter Loan Amount");
      return false;
    }
    if (!amountPerMonth || parseFloat(amountPerMonth) === 0) {
      toast.warn("Please Enter Amount Per Month");
      return false;
    }
    if (!totalMonth) {
      toast.warn("Please Enter Total Month");
      return false;
    }
    if (!balanceAmount) {
      toast.warn("Please Enter Balance Amount");
      return false;
    }
    if (!loanDate) {
      toast.warn("Please Enter Loan Date");
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validate()) return;

    try {
      setLoading(true);

      const payload = {
        branchId: branchId,
        empCode: empCode,
        empName: empName,
        loanCode: loanCode,
        loanAmount: parseFloat(loanAmount),
        amountPerMonth: parseFloat(amountPerMonth),
        totalMonth: parseInt(totalMonth),
        balanceAmount: parseFloat(balanceAmount),
        loanDate: loanDate,
        closeLoan: closeLoan,
        closedDate: closedDate || null,
        closedAmount: parseFloat(closedAmount) || 0,
        remarks: remarks,
        userId: userId,
        ipAddress: getClientIP(),
      };

      let res;
      if (editingLoanId) {
        res = await axios.put(
          `${BASE_URL}/api/employee-loan-entry/${editingLoanId}`,
          payload,
          { params: { finYearId: Number(finYearId) } }
        );
      } else {
        res = await axios.post(
          `${BASE_URL}/api/employee-loan-entry`,
          payload,
          { params: { finYearId: Number(finYearId) } }
        );
      }

      if (res?.data?.success) {
        toast.success(res.data.message);
        setTimeout(() => {
          window.location.href = "/PayRoll/EmployeeLoanListing";
        }, 1500);
      } else {
        toast.error(res?.data?.message || "Operation failed");
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Something went wrong";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <PayRollLayoutWrapper>
      <ToastContainer
        position="top-right"
        autoClose={2800}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        theme={isDark ? "dark" : "light"}
      />

      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">EMPLOYEE LOAN ENTRY</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`mx-auto max-w-7xl p-6 mt-6 rounded-3xl shadow-2xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `${currentSkin.glass} bg-white text-black border-gray-200`
          }`}
        >
          {/* Top row */}
          <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4 mb-6">
            <div className="flex flex-wrap items-center gap-4">
              <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow ${isDark ? "bg-white/10" : "bg-gray-100"}`}>
                <Banknote className={isDark ? "text-white" : "text-gray-700"} />
              </div>

              <div>
                <div className={`text-2xl font-extrabold ${isDark ? "text-white" : "text-gray-900"}`}>
                  {editingLoanId ? "Edit Loan" : "Add New Loan"}
                </div>
                <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  {editingLoanId ? "Update existing loan details" : "Create a new employee loan"}
                </div>
              </div>

              <div className={`px-4 py-2 rounded-2xl text-sm font-bold ${isDark ? "bg-blue-900/40 text-blue-100" : "bg-blue-100 text-blue-800"}`}>
                {screenBadge}
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 justify-end">
              <button onClick={handleBack} className={ghostBtn}>
                <ArrowLeft className="w-4 h-4" />
                Back
              </button>
            </div>
          </div>

          {/* Form */}
          <div className={cardBase + " p-6"}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {/* Employee Select */}
              <div>
                <label className={labelBase}>
                  Employee <span className="text-red-500">*</span>
                </label>
                <div className="mt-2 relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <User className="w-4 h-4 text-gray-400" />
                  </div>
                  <select
                    value={empId}
                    onChange={(e) => handleEmployeeChange(e.target.value)}
                    className={`pl-11 ${empSelectDisabled ? inputDisabled : inputBase}`}
                    disabled={empSelectDisabled}
                  >
                    <option value="">Select Employee</option>
                    {employees.map((emp) => (
                      <option key={emp.fld_Employeeid} value={emp.fld_Employeeid}>
                        {emp.fld_Employeecode} - {emp.fld_Employee}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Employee Code (Display Only) */}
              <div>
                <label className={labelBase}>Employee Code</label>
                <div className="mt-2 relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Hash className="w-4 h-4 text-gray-400" />
                  </div>
                  <input
                    value={empCode}
                    className={`pl-11 ${inputDisabled}`}
                    disabled
                    placeholder="Auto-filled"
                  />
                </div>
              </div>

              {/* Employee Name (Display Only) */}
              <div>
                <label className={labelBase}>Employee Name</label>
                <div className="mt-2 relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Type className="w-4 h-4 text-gray-400" />
                  </div>
                  <input
                    value={empName}
                    className={`pl-11 ${inputDisabled}`}
                    disabled
                    placeholder="Auto-filled"
                  />
                </div>
              </div>

              {/* Loan Code */}
              <div>
                <label className={labelBase}>
                  Loan Code <span className="text-red-500">*</span>
                </label>
                <div className="mt-2 relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Hash className="w-4 h-4 text-gray-400" />
                  </div>
                  <input
                    value={loanCode}
                    className={`pl-11 ${inputDisabled}`}
                    disabled
                  />
                </div>
              </div>

              {/* Loan Amount */}
              <div>
                <label className={labelBase}>
                  Loan Amount <span className="text-red-500">*</span>
                </label>
                <div className="mt-2 relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <DollarSign className="w-4 h-4 text-gray-400" />
                  </div>
                  <input
                    type="number"
                    step="0.01"
                    value={loanAmount}
                    onChange={(e) => setLoanAmount(e.target.value)}
                    placeholder="0.00"
                    className={`pl-11 ${inputBase}`}
                  />
                </div>
              </div>

              {/* Amount Per Month */}
              <div>
                <label className={labelBase}>
                  Amount Per Month <span className="text-red-500">*</span>
                </label>
                <div className="mt-2 relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <DollarSign className="w-4 h-4 text-gray-400" />
                  </div>
                  <input
                    type="number"
                    step="0.01"
                    value={amountPerMonth}
                    onChange={(e) => setAmountPerMonth(e.target.value)}
                    placeholder="0.00"
                    className={`pl-11 ${inputBase}`}
                  />
                </div>
              </div>

              {/* Total Month */}
              <div>
                <label className={labelBase}>
                  Total Month <span className="text-red-500">*</span>
                </label>
                <div className="mt-2 relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Hash className="w-4 h-4 text-gray-400" />
                  </div>
                  <input
                    type="number"
                    value={totalMonth}
                    className={`pl-11 ${inputDisabled}`}
                    disabled
                  />
                </div>
              </div>

              {/* Balance Amount */}
              <div>
                <label className={labelBase}>
                  Balance Amount <span className="text-red-500">*</span>
                </label>
                <div className="mt-2 relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <DollarSign className="w-4 h-4 text-gray-400" />
                  </div>
                  <input
                    type="number"
                    step="0.01"
                    value={balanceAmount}
                    className={`pl-11 ${inputDisabled}`}
                    disabled
                  />
                </div>
              </div>

              {/* Loan Date */}
              <div>
                <label className={labelBase}>
                  Date <span className="text-red-500">*</span>
                </label>
                <div className="mt-2 relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Calendar className="w-4 h-4 text-gray-400" />
                  </div>
                  <input
                    type="date"
                    value={loanDate}
                    onChange={(e) => setLoanDate(e.target.value)}
                    className={`pl-11 ${inputBase}`}
                  />
                </div>
              </div>
            </div>

            {/* Close Loan Section */}
            {editingLoanId && (
              <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-3 mb-4">
                  <input
                    type="checkbox"
                    id="closeLoan"
                    checked={closeLoan}
                    onChange={(e) => handleCloseLoanChange(e.target.checked)}
                    className="w-5 h-5 rounded"
                  />
                  <label htmlFor="closeLoan" className={`${labelBase} cursor-pointer`}>
                    Close Loan
                  </label>
                </div>

                {closeLoan && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-4">
                    {/* Closed Date */}
                    <div>
                      <label className={labelBase}>
                        Closed Date <span className="text-red-500">*</span>
                      </label>
                      <div className="mt-2 relative">
                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                          <Calendar className="w-4 h-4 text-gray-400" />
                        </div>
                        <input
                          type="date"
                          value={closedDate}
                          onChange={(e) => setClosedDate(e.target.value)}
                          className={`pl-11 ${inputBase}`}
                        />
                      </div>
                    </div>

                    {/* Closed Amount */}
                    <div>
                      <label className={labelBase}>
                        Closed Amount <span className="text-red-500">*</span>
                      </label>
                      <div className="mt-2 relative">
                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                          <DollarSign className="w-4 h-4 text-gray-400" />
                        </div>
                        <input
                          type="number"
                          step="0.01"
                          value={closedAmount}
                          onChange={(e) => setClosedAmount(e.target.value)}
                          className={`pl-11 ${inputBase}`}
                        />
                      </div>
                    </div>

                    {/* Remarks */}
                    <div className="md:col-span-2">
                      <label className={labelBase}>
                        Remarks <span className="text-red-500">*</span>
                      </label>
                      <div className="mt-2">
                        <textarea
                          value={remarks}
                          onChange={(e) => setRemarks(e.target.value)}
                          placeholder="Enter remarks..."
                          rows={3}
                          className={inputBase}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3 mt-6 justify-center">
              <button onClick={handleSave} className={primaryBtn} disabled={loading}>
                <Save className="w-4 h-4" />
                {editingLoanId ? "Update" : "Save"}
              </button>

              <button onClick={handleReset} className={ghostBtn}>
                <RefreshCcw className="w-4 h-4" />
                Reset
              </button>
            </div>
          </div>
        </div>
      </div>
    </PayRollLayoutWrapper>
  );
};

export default EmployeeLoanEntry;
